import React from 'react';
import * as videoStyles from '../../styles/communs/header-video.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faArrowDown} from '@fortawesome/free-solid-svg-icons';
import AppButton from './AppButton';
import { useMediaQuery } from '../../hooks/useMediaQuery';

import {GatsbyImage, getImage} from 'gatsby-plugin-image';
import { followLink } from '../../utils/utils';
import IconContainer from './IconContainer';
import { useTranslation } from 'react-i18next';
import Image from './Image';

/*
    valeur attendu ====>

    enum video_display_mode {
        "BASIC",
        "FULL_SCREEN"
    }

    enum title_position {
        "TOP",
        "BOTTOM",
        "CENTER"
    }

    enum title_alignement {
        "LEFT",
        "CENTER",
        "RIGHT"
    }

    brightness 0.0 < - > 1
    show_alt_image_when_resolution_below => value in px
    override_style => css string

*/

const HeaderVideo = (props) => {
  const {t} = useTranslation();
  const {
    title,
    title_position,
    title_alignement,
    title_opacity = 1,
    show_alt_image_when_resolution_below,
    action_buttons: buttons,
    override_style,
    image_alternative,
    brightness,
    video_display_mode,
    video : srcVideo,
    scroll_text,
    scroll_type,
    logo,
  } = props;

  const finalScrollType = scroll_type || 'TEXT';
  const finalScrollText = scroll_text || t('header.video.scroll');

  const altNN = () => {
    return '';
  };

  const videoRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const smartphone = useMediaQuery(`(max-width: ${show_alt_image_when_resolution_below}px)`);

  const formatStringToCamelCase = str => {
    const splitted = str.split('-');
    if (splitted.length === 1)
      return splitted[0];
    return (
      splitted[0] +
            splitted
              .slice(1)
              .map(word => word[0].toUpperCase() + word.slice(1))
              .join('')
    );
  };

  const getStyleObjectFromString = str => {
    if (!str)
      return {};
    const style = {};
    str.split(';').forEach(el => {
      const [property, value] = el.split(':');
      if (!property)
        return;
      const formattedProperty = formatStringToCamelCase(property.trim());
      style[formattedProperty] = value.trim();
    });
    return style;
  };


  const handleClick = () => {
    if (isPlaying){
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const videoStyle = {
    filter: `brightness(${brightness})`,
  };
  return (
    <div className={clsx({
      [videoStyles.videoBloc]: video_display_mode === 'BASIC',
      [videoStyles.videoBlocfullScreen]: video_display_mode === 'FULL_SCREEN'})}
    style={(video_display_mode === 'BASIC' ? getStyleObjectFromString(override_style) : {})}
    >
      {(!srcVideo || smartphone === true) && image_alternative &&
        <GatsbyImage
          alt={altNN('')}
          className={videoStyles.image}
          image={getImage(image_alternative.imageFile)}
          loading="eager"
          style={{position: 'absolute', top: '0px', bottom: '0px'}}
        />
      }
      {smartphone === false && srcVideo &&
        <video
          autoPlay
          className={videoStyles.video}
          id="video"
          loop
          muted
          playsInline
          ref={videoRef}
          src={srcVideo}
          style={videoStyle}
        >
            Your browser does not support the video tag.
        </video>}
      <div className={videoStyles.videoHeaderAbsolute}>
        <div className={clsx(videoStyles.videoHeaderContent,
          {[videoStyles.contentTop]: title_position === 'TOP',
            [videoStyles.contentCenter]: title_position === 'CENTER',
            [videoStyles.contentBot]: title_position === 'BOTTOM',
            [videoStyles.contentLeft]: title_alignement === 'LEFT',
            [videoStyles.contentAlignCenter]: title_alignement === 'CENTER',
            [videoStyles.contentRight]: title_alignement === 'RIGHT'})}
        >
          {logo &&
            <Image
              alt={logo.alternativeText || logo.alternativeTextEN || ''}
              className={videoStyles.logo}
              image={{url: logo.imageFile.publicURL}}
            />
          }
          {title && title.trim() !== '' &&
            <h1
              className={videoStyles.title}
              style={{backgroundColor: `rgba(255, 255, 255, ${title_opacity})`}}
            >
              {title}
            </h1>
          }
          <div className={videoStyles.buttonContainer}
            style={{justifyContent: `${title_alignement.toLowerCase()}`}}
          >
            {buttons.map((button, i) => (
              <AppButton
                className={videoStyles.buttonVideo}
                classNameChildren={videoStyles.buttonText}
                color={(button.color === 'RED' ? 'secondary' : 'primary')}
                key={`link-video-${i}`}
                onClick={() => { followLink(button.URL, (button.new_window === true) ? '_blank' : '_self'); }}
                opacity={button.button_opacity}
                padding={5}
                paddingWidth={10}
                rightIcon={
                  <IconContainer color="WHITE"
                    icon={button.icon}
                    size={25}
                  />
                }
                style={{textAlign: `${title_alignement.toLowerCase()}`}}
                url={button.URL || '#'}
                variant={'impact'}
              >
                {button.label}
              </AppButton>
            ))}
          </div>
        </div>
      </div>
      {video_display_mode === 'FULL_SCREEN' && finalScrollType === 'TEXT' &&
      <div className={clsx(
        videoStyles.scrollContainer,
        {[videoStyles.textLeft]: title_alignement === 'LEFT',
          [videoStyles.textRight]: title_alignement === 'RIGHT',
          [videoStyles.textCenter]: title_alignement === 'CENTER'})}
      >
        <div className={videoStyles.scrollText}>
          <AppButton
            className={videoStyles.buttonScroll}
            color="secondary"
            onClick={() => { followLink('#main-content-page', '_self'); }}
            padding={5}
            paddingWidth={10}
            variant="impact"
          >
            {finalScrollText}

          </AppButton>
        </div>
        <div className={videoStyles.scrollBar}/>
      </div>}
      {video_display_mode === 'FULL_SCREEN' && finalScrollType === 'ARROW' &&
      <div className={videoStyles.arrowContainer}>
        <AppButton
          className={videoStyles.scrollArrow}
          color="secondary"
          label="scroll button"
          onClick={() => { followLink('#main-content-page', '_self'); }}
          variant="contained"
        >
          <FontAwesomeIcon
            className={videoStyles.icon}
            icon={faArrowDown}
          />
        </AppButton>
      </div>}
      {!smartphone &&
      <div className={videoStyles.iconContainer}>
        <AppButton
          className={videoStyles.buttonPlay}
          color="secondary"
          label="play button"
          variant="contained"
        >
          {!isPlaying &&
          <FontAwesomeIcon className={videoStyles.icon}
            icon={faPlay}
            onClick={handleClick}
          />
          }
          {isPlaying &&
          <FontAwesomeIcon className={videoStyles.icon}
            icon={faPause}
            onClick={handleClick}
          />
          }
        </AppButton>
      </div>
      }
    </div>
  );
};

HeaderVideo.propTypes = {
  action_buttons: PropTypes.arrayOf(
    PropTypes.shape({
      URL: PropTypes.string,
      color: PropTypes.string,
      icon: PropTypes.string,
      label: PropTypes.string,
      new_window: PropTypes.bool,
      button_opacity: PropTypes.number,
    })
  ),
  brightness: PropTypes.number,
  image_alternative: PropTypes.shape({
    imageFile: PropTypes.object,
  }),
  logo: PropTypes.shape({
    alternativeText: PropTypes.string,
    alternativeTextEN: PropTypes.string,
    imageFile: PropTypes.shape({
      publicURL: PropTypes.string,
    }),
  }),
  override_style: PropTypes.string,
  scroll_text: PropTypes.string,
  scroll_type: PropTypes.oneOf(['TEXT', 'ARROW', 'NONE']),
  show_alt_image_when_resolution_below: PropTypes.number,
  title: PropTypes.string,
  title_alignement: PropTypes.oneOf(['LEFT', 'CENTER', 'RIGHT']),
  title_opacity: PropTypes.number,
  title_position: PropTypes.oneOf(['TOP', 'BOTTOM', 'CENTER']),
  video: PropTypes.string,
  video_display_mode: PropTypes.oneOf(['BASIC', 'FULL_SCREEN']),
};

export default HeaderVideo;